import { HttpClient, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  signal
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { FileUploadHandlerEvent } from 'primeng/fileupload';
import { Subject, takeUntil } from 'rxjs';

import { ACCEPT } from './file-upload.const';
import { cancelUpload } from './file-upload.helpers';
import { IUploadProgress, UploadStatus } from './file-upload.types';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FileUploadComponent implements OnDestroy {
  @Input() accept: string = ACCEPT;
  @Input() uploadUrl: string;
  @Input() multiple: boolean = true;

  @Output() uploadFiles = new EventEmitter<FileUploadHandlerEvent>();
  @Output() allCompleted = new EventEmitter<File[]>();
  @Output() completed = new EventEmitter<HttpResponse<any>>();

  private allUploadsComplete = signal(false);
  private uploadFinished = signal(false);
  public files: File[] = [];
  public uploadProgress: IUploadProgress[] = [];

  handleCancelUpload = cancelUpload;

  private readonly destroyed$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private httpClient: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly messageService: MessageService
  ) {}

  updateFiles(files: File[]) {
    this.files = files;
  }

  addFiles(uploadHandler: FileUploadHandlerEvent) {
    this.allUploadsComplete.set(false);

    const promises = uploadHandler.files.map((file, index) => {
      if (!this.uploadProgress[index]) {
        this.uploadProgress[index] = {
          fileName: file.name,
          progress: 0,
          status: UploadStatus.PENDING
        };

        return this.uploadFile(file, index);
      }
      return this.uploadProgress[index].subscription;
    });

    Promise.allSettled(promises).then(() => {
      this.allUploadsComplete.set(true);
      this.uploadFinished.set(true);
      this.allCompleted.emit(this.files);
      this.files = [];
      this.uploadProgress = [];
      this.changeDetectorRef.detectChanges();
    });
  }

  uploadFile(file: File, index: number): Promise<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return new Promise<void>((resolve, reject) => {
      const subscription = this.httpClient
        .post(this.uploadUrl, formData, {
          reportProgress: true,
          observe: 'events'
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (event: HttpEvent<unknown>) => {
            if (![UploadStatus.CANCELLED, UploadStatus.ERROR].includes(this.uploadProgress[index]?.status)) {
              switch (event.type) {
                case HttpEventType.UploadProgress:
                  this.updateProgress(index, Math.round((event.loaded / event.total) * 100));
                  break;
                case HttpEventType.Response:
                  this.updateProgress(index, 100);

                  subscription.unsubscribe();
                  this.completed.emit(event);
                  resolve();
                  break;
              }
            } else {
              subscription.unsubscribe();
              resolve();
            }
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              detail: error.error.message,
              life: 3000
            });
            this.updateProgress(index, 0);
            subscription.unsubscribe();
            reject(error);
          }
        });

      this.uploadProgress[index].subscription = subscription;

      return subscription;
    });
  }

  updateProgress(index: number, progress: number) {
    this.uploadProgress[index] = {
      ...this.uploadProgress[index],
      progress,
      status: progress < 100 ? UploadStatus.UPLOADING : UploadStatus.COMPLETE
    };
    this.changeDetectorRef.detectChanges();
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
